import { FabricEditor, FabricEditorCanvas, FabricEditorProvider } from 'modules/fabric-editor';
import {
  applyLayoutBackgrounds,
  centerMatchBundles,
  filterMatchScopedObjects,
  isRenderImageError,
  substitutePlaceholders,
} from 'modules/manager/domain/render';
import mediaManagerRepository from 'modules/manager/domain/repositories/media-manager-repository';
import { fetchTemplateOnly, resetEditor, selectActiveTemplate, selectError } from 'modules/manager/store';
import { useGetRenderContextQuery } from 'modules/manager/store/render-api';
import { skipToken } from '@reduxjs/toolkit/query';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { RENDER_PLUGINS, StatusOverlay, collectBgUrls, loadCanvasJson, nextFrame, parseId } from './render-shared';

const TemplateRenderPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const [params] = useSearchParams();

  const matchIds = useMemo(() => collectMatchIds(params), [params]);
  // URL contract: ?templateId=N (was: ?boardId=N — kept as-is for backwards compat with external callers
  // by also reading the legacy `boardId` query key when the new one is absent).
  const templateId = parseId(params.get('templateId') ?? params.get('boardId'));
  const bgUrls = useMemo(() => collectBgUrls(params), [params]);

  const activeTemplate = useAppSelector(selectActiveTemplate);
  const loadError = useAppSelector(selectError);
  const ctxQuery = useGetRenderContextQuery(matchIds.length > 0 ? { matchIds } : skipToken);

  const [editor, setEditor] = useState<FabricEditor | null>(null);
  const [status, setStatus] = useState<'loading' | 'rendering' | 'done' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const orchestrated = useRef(false);

  useEffect(() => {
    if (templateId === null) {
      setStatus('error');
      setErrorMessage('Missing templateId in URL');
      return;
    }
    dispatch(fetchTemplateOnly({ templateId }));
    return () => {
      dispatch(resetEditor());
    };
  }, [dispatch, templateId]);

  useEffect(() => {
    if (matchIds.length === 0) {
      setStatus('error');
      setErrorMessage('Missing matchId in URL');
    }
  }, [matchIds]);

  useEffect(() => {
    if (orchestrated.current) return;
    if (!editor || !activeTemplate || !ctxQuery.data) return;
    if (loadError) {
      console.error(loadError);
      setStatus('error');
      setErrorMessage(loadError);
      return;
    }
    orchestrated.current = true;
    setStatus('rendering');

    (async () => {
      try {
        await loadCanvasJson(editor, activeTemplate.version, activeTemplate.objects ?? []);
        await applyLayoutBackgrounds(editor, bgUrls);
        await filterMatchScopedObjects(editor, ctxQuery.data!);
        await centerMatchBundles(
          editor,
          ctxQuery.data!,
          activeTemplate.matchLayout,
          activeTemplate.objects ?? [],
        );
        await substitutePlaceholders(editor, ctxQuery.data!, {
          templateType: activeTemplate.templateType,
          templateSubtypes: activeTemplate.templateSubtypes,
        });
        editor.canvas.requestRenderAll();
        await nextFrame();
        const dataUrl = editor.canvas.toDataURL({ format: 'png', multiplier: 1 });
        await mediaManagerRepository.persistRenderedImage({
          matchId: matchIds[0],
          templateId: activeTemplate.id,
          dataUrl,
        });
        setStatus('done');
      } catch (err) {
        console.error(err);
        const userMessage = isRenderImageError(err)
          ? err.userMessage
          : err instanceof Error
            ? err.message
            : String(err);
        setStatus('error');
        setErrorMessage(userMessage);
      }
    })();
  }, [editor, activeTemplate, ctxQuery.data, loadError, bgUrls]);

  useEffect(() => {
    if (ctxQuery.error) {
      console.error(ctxQuery.error);
      setStatus('error');
      setErrorMessage((ctxQuery.error as { message?: string }).message ?? 'Failed to load render context');
    }
  }, [ctxQuery.error]);

  if (!activeTemplate) {
    return <StatusOverlay status={status} message={errorMessage ?? 'Loading template…'} title={statusTitle} />;
  }

  return (
    <>
      <div style={{ position: 'fixed', left: -99999, top: -99999, pointerEvents: 'none' }} aria-hidden>
        <FabricEditorProvider editor={editor}>
          <FabricEditorCanvas
            key={`${activeTemplate.id}-${activeTemplate.width}x${activeTemplate.height}`}
            width={activeTemplate.width}
            height={activeTemplate.height}
            resolution={{ width: activeTemplate.width, height: activeTemplate.height }}
            plugins={RENDER_PLUGINS}
            onReady={setEditor}
          />
        </FabricEditorProvider>
      </div>
      <StatusOverlay status={status} message={errorMessage} title={statusTitle} />
    </>
  );
};

function statusTitle(status: string): string {
  switch (status) {
    case 'loading': return 'Loading template and match data…';
    case 'rendering': return 'Rendering PNG…';
    case 'done': return 'Image saved successfully.';
    case 'error': return 'Render failed';
    default: return status;
  }
}

// URL contract for multi-match templates: ?matchId1=A&matchId2=B&matchId3=C
// (1-based, mirrors the bg1..bgN pattern). Single-match templates fall back
// to ?matchId=N. Order is preserved: matches[i-1] resolves placeholders with
// matchNumber=i.
function collectMatchIds(params: URLSearchParams): number[] {
  const out: number[] = [];
  for (let i = 1; i <= 50; i++) {
    const raw = params.get(`matchId${i}`);
    if (raw === null) {
      if (i === 1) continue;
      break;
    }
    const id = parseId(raw);
    if (id !== null) out.push(id);
  }
  if (out.length > 0) return out;
  const legacy = parseId(params.get('matchId'));
  return legacy !== null ? [legacy] : [];
}

export default TemplateRenderPage;
